import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import startCase from 'lodash/startCase';
import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import NumbersIcon from '@mui/icons-material/Numbers';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardIos';
import ImageIcon from '@mui/icons-material/Image';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import FolderIcon from '@mui/icons-material/Folder';
import AbcIcon from '@mui/icons-material/Abc';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DateRangeIcon from '@mui/icons-material/DateRange';
import Rtt from '@mui/icons-material/Rtt';

import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import Button from 'components/Button';
import AnchorMenu from 'components/AnchorMenu';
import EditAttributeField from 'components/EditAttributeField';
import InputField, { INPUT_TYPE_BOOLEAN, INPUT_TYPE_DATETIME, INPUT_TYPE_NUMBER, INPUT_TYPE_TEXTAREA, INPUT_TYPE_FILE, INPUT_TYPE_IMAGE, INPUT_TYPE_VIDEO, INPUT_TYPE_AUDIO, INPUT_TYPE_TEXT, INPUT_TYPE_RICH_TEXT } from 'components/InputField';
import { getCollectionDefinition, createCollectionRouting, getAttributes } from 'api/collection_definitions';
import { getDynamicTableRows, setUserObjectCache, updateDynamicTableRow, createDynamicTableRow, deleteDynamicTableRow, copyDynamicTableRow } from 'api/sections';
import { getObjectTypes, getPublicObjectTypes } from 'api/object_types';
import { handleErrorResponse, isUuid, parseJSON } from 'utils/general';
import { getDynamicData, fetchSectionProps } from 'utils/sections';
import { withLoading } from 'utils/hoc';
import { useGlobalData } from 'contexts/global-data';
import { useAuth } from 'contexts/auth';

const mandatoryAttributeNames = ['key', 'description'];
const defaultAttributeNames = ['redirect_collection_definition_id', 'redirect_object_id', 'id', 'type', 'createddate', 'lastmoddate', 'created_by', 'modified_by'];
const localStorageKeys = {
  showKeyAndDescription: 'dynamicTableAttributeView-showKeyAndDescription-Switch',
  showCopyDeleteButtons: 'dynamicTableAttributeView-showCopyDeleteButtons-Switch',
};

const parseData = (data, attributes, redirectAttributes) => {
  return data.map((datum) => {
    const { json_short_data, json_big_data, ...restDatum } = datum;
    const dynamicData = getDynamicData(datum, attributes, redirectAttributes);
    return {
      ...restDatum,
      ...dynamicData,
    };
  });
};

const getUnknowAttributeIds = (data, attributes) => {
  return (data || []).reduce((unknownIds, datum) => {
    const jsonStrValue = get(datum, 'json_short_data') || get(datum, 'json_big_data');
    const jsonValue = parseJSON(jsonStrValue, {});
    const datumUnknownIds = [];
    Object.keys(jsonValue).forEach((jsonValueAttrId) => {
      const attr = attributes.find(({ attribute_id }) => attribute_id === jsonValueAttrId);
      if (!attr) {
        datumUnknownIds.push(jsonValueAttrId);
      }
    });
    return [...unknownIds, ...datumUnknownIds];
  }, []);
};

const getFormValues = (parsedRowData, objectTypes) => {
  return parsedRowData.reduce((values, parsedRowDatum) => {
    const newValues = { ...values };
    const objectType = objectTypes.find(({ object_type_id }) => object_type_id === Number(parsedRowDatum.type));
    let parsedValue = get(parsedRowDatum, 'value');
    if ([3, 4, 15, 16].includes(objectType?.object_type_id)) {
      parsedValue = Number(parsedValue);
    }
    newValues[`${parsedRowDatum.id}-name`] = get(parsedRowDatum, 'name');
    newValues[`${parsedRowDatum.id}-value`] = parsedValue;
    newValues[`${parsedRowDatum.id}-__system_order__`] = Number(get(parsedRowDatum, '__system_order__'));
    mandatoryAttributeNames.forEach((attributeName) => {
      newValues[`${parsedRowDatum.id}-${attributeName}`] = get(parsedRowDatum, attributeName);
    });
    Object.keys(parsedRowDatum).forEach((parsedRowDatumKey) => {
      if (
        !(`${parsedRowDatum.id}-${parsedRowDatumKey}` in newValues)
        && !defaultAttributeNames.includes(parsedRowDatumKey)
        && !mandatoryAttributeNames.includes(parsedRowDatumKey)
      ) {
        const parsedRowDatumValue = get(parsedRowDatum, parsedRowDatumKey);
        newValues[`${parsedRowDatum.id}-${parsedRowDatumKey}`] = parsedRowDatumValue;
        if (['number', 'boolean'].includes(typeof parsedRowDatumValue)) {
          newValues[`${parsedRowDatum.id}-${parsedRowDatumKey}`] = `${parsedRowDatumValue}`;
        } else if (parsedRowDatumValue && typeof parsedRowDatumValue === 'object') {
          newValues[`${parsedRowDatum.id}-${parsedRowDatumKey}`] = JSON.stringify(parsedRowDatumValue, 0, 2);
        }
      }
    });
    return newValues;
  }, {});
};

const DynamicTableAttributeViewSection = ({ collection_definition_id, addable, deletable, copyable, type_routing, fields_config, add_button_label, delete_button_label, copy_button_label, paste_button_label, disable_title, section, setLoading, ...props }) => {
  const [collectionDefinition, setCollectionDefinition] = useState(null);
  const [extraAttributes, setExtraAttributes] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [updatedTypeRouting, setUpdatedTypeRouting] = useState(type_routing || {});
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [formInitialized, setFormInitialized] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const { objectTypes, setObjectTypes, copiedDynamicObj, setCopiedDynamicObj } = useGlobalData();
  const { auth, setAuth } = useAuth();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const bottomDiv = useRef(null);

  const userRoleScopes = useMemo(() => auth.user?.role?.scopes || [], [auth.user]);
  const hasAddDataRight = useMemo(() => !!userRoleScopes.find(({ scope }) => scope === 'add_collection_values'), [userRoleScopes]);
  const hasEditDataRight = useMemo(() => !!userRoleScopes.find(({ scope }) => scope === 'edit_collection_values'), [userRoleScopes]);
  const hasDeleteDataRight = useMemo(() => !!userRoleScopes.find(({ scope }) => scope === 'delete_collection_values'), [userRoleScopes]);

  const [showKeyAndDescription, setShowKeyAndDescription] = useState(JSON.parse(localStorage.getItem(localStorageKeys.showKeyAndDescription)) || false);
  const [showCopyDeleteButtons, setShowCopyDeleteButtons] = useState(JSON.parse(localStorage.getItem(localStorageKeys.showCopyDeleteButtons)) || false);

  const parsedRowData = useMemo(() => parseData(rowData, collectionDefinition?.attributes, extraAttributes), [rowData, collectionDefinition?.attributes, extraAttributes]);

  const nameAttribute = useMemo(() => (collectionDefinition?.attributes || []).find(({ name }) => name === 'name'), [collectionDefinition?.attributes]);
  const valueAttribute = useMemo(() => (collectionDefinition?.attributes || []).find(({ name }) => name === 'value'), [collectionDefinition?.attributes]);
  const systemOrderAttribute = useMemo(() => (collectionDefinition?.attributes || []).find(({ name }) => name === '__system_order__'), [collectionDefinition?.attributes]);

  const onOpenMenu = useCallback(({ currentTarget }) => setMenuAnchor(currentTarget), []);

  const onClickCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const fetchObjectTypesFromApi = useCallback(() => {
    const objectTypeParams = {
      $orderBy: 'object_type_id',
    };
    setLoading(true);
    return (auth.user ? getObjectTypes(objectTypeParams) : getPublicObjectTypes(objectTypeParams))
      .then(({ data }) => {
        setObjectTypes(data);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [auth.user, setObjectTypes, setLoading, setAuth]);

  const updateFormValues = useCallback((data) => {
    if (formRef.current) {
      const updatedParsedRowData = parseData(data, collectionDefinition?.attributes, extraAttributes);
      const updatedFormValues = getFormValues(updatedParsedRowData, objectTypes);
      formRef.current.setValues((oriValues) => {
        return Object.keys(updatedFormValues).reduce((values, field) => {
          const updatedValues = { ...values };
          if (field in oriValues) {
            updatedValues[field] = oriValues[field];
          } else {
            updatedValues[field] = updatedFormValues[field];
          }
          return updatedValues;
        }, {});
      });
    }
  }, [collectionDefinition?.attributes, extraAttributes, objectTypes]);

  const fetchDataFromApi = useCallback((update) => {
    if (collectionDefinition?.collection_definition_id) {
      const dynamicTableParams = {
        $orderBy: 'createddate asc',
      };
      setLoading(true);
      return getDynamicTableRows(collectionDefinition.collection_definition_id, dynamicTableParams)
        .then(({ data }) => {
          setRowData(data);
          setDataLoaded(true);
          if (update) {
            updateFormValues(data);
          } else {
            setFormInitialized(false);
          }
        })
        .catch((err) => {
          handleErrorResponse(err, setAuth);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [collectionDefinition, updateFormValues, setLoading, setAuth]);

  const fetchCollectionDefinitionFromApi = useCallback(() => {
    if (isUuid(collection_definition_id)) {
      const collectionDefinitionParams = {
        $expand: 'attributes',
      };
      setLoading(true);
      return getCollectionDefinition(collection_definition_id, collectionDefinitionParams)
        .then(({ data }) => {
          setCollectionDefinition(data);
        })
        .catch((err) => {
          handleErrorResponse(err, setAuth);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [collection_definition_id, setLoading, setAuth]);

  const fetchAttributesFromApi = useCallback((attributeIds) => {
    const attributeParams = {
      'attribute_id[in]': attributeIds.join(','),
    };
    return getAttributes(attributeParams)
      .then(({ data }) => {
        return data;
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      });
  }, [setAuth]);

  const onCreateAttribute = useCallback((objTypeId) => {
    if (!collectionDefinition) {
      return Promise.resolve();
    }
    const createBody = {
      type: objTypeId,
    };
    setLoading(true);
    return createDynamicTableRow(collectionDefinition.collection_definition_id, createBody)
      .then(({ data }) => {
        if (objTypeId <= 0 && !updatedTypeRouting.default && !(data.type in updatedTypeRouting)) {
          const createRoutingBody = {
            object_type_id: data.type,
            from_section_id: section?.section_id,
          };
          createCollectionRouting(createRoutingBody)
            .then(({ data: newRouting }) => {
              setUpdatedTypeRouting((oriTypeRouting) => ({
                ...oriTypeRouting,
                ...newRouting,
              }));
            })
            .catch((err) => {
              handleErrorResponse(err, setAuth);
            });
        }
        const objectType = objectTypes.find(({ object_type_id }) => object_type_id === Number(data.type));
        if (!objectType) {
          fetchObjectTypesFromApi();
        }
        return fetchDataFromApi(true)
          .then(() => {
            if (bottomDiv.current) {
              setTimeout(() => {
                bottomDiv.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
              }, 500);
            }
          });
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [collectionDefinition, section, objectTypes, updatedTypeRouting, fetchDataFromApi, fetchObjectTypesFromApi, setLoading, setAuth]);

  const onEditAttribute = useCallback((selectedId) => {
    if (!collectionDefinition) {
      return Promise.resolve();
    }
    const selectedRow = rowData.find(({ id }) => id === selectedId);
    const dynamicData = getDynamicData(selectedRow, collectionDefinition.attributes);
    setLoading(true);
    return Promise.all([
      setUserObjectCache(collectionDefinition.collection_definition_id, selectedId),
      (dynamicData?.redirect_collection_definition_id && dynamicData?.redirect_object_id) ? (
        setUserObjectCache(dynamicData.redirect_collection_definition_id, dynamicData.redirect_object_id)
      ) : null,
    ])
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [collectionDefinition, rowData, setLoading, setAuth]);

  const onDeleteAttribute = useCallback((datumId) => {
    if (!collectionDefinition) {
      return Promise.resolve();
    }
    const selectedRow = rowData.find(({ id }) => id === datumId);
    const dynamicData = getDynamicData(selectedRow, collectionDefinition.attributes);
    setLoading(true);
    return deleteDynamicTableRow(collectionDefinition.collection_definition_id, datumId)
      .then(() => {
        if (dynamicData?.redirect_collection_definition_id && !dynamicData?.redirect_object_id) {
          fetchObjectTypesFromApi();
        }
        return fetchDataFromApi(true);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [collectionDefinition, rowData, fetchDataFromApi, fetchObjectTypesFromApi, setLoading, setAuth]);

  const onCopyAttribute = useCallback((datumId) => {
    if (!collectionDefinition) {
      return Promise.resolve();
    }
    const selectedRow = rowData.find(({ id }) => id === datumId);
    const dynamicData = getDynamicData(selectedRow, collectionDefinition.attributes);
    return setCopiedDynamicObj({
      id: datumId,
      collection_definition_id: collectionDefinition.collection_definition_id,
      is_collection: dynamicData?.redirect_collection_definition_id && !dynamicData?.redirect_object_id,
    });
  }, [rowData, collectionDefinition, setCopiedDynamicObj]);

  const onPasteAttribute = useCallback(() => {
    if (!copiedDynamicObj) {
      return Promise.resolve();
    }
    const copyRowBody = {
      from_section_id: section?.section_id,
      destination_collection_definition_id: collectionDefinition?.collection_definition_id,
    };
    setLoading(true);
    return copyDynamicTableRow(copiedDynamicObj.collection_definition_id, copiedDynamicObj.id, copyRowBody)
      .then(({ data }) => {
        if (!updatedTypeRouting.default && !(data.type in updatedTypeRouting)) {
          fetchSectionProps(section, !auth.user)
            .then((updatedSectionProps) => {
              setUpdatedTypeRouting(updatedSectionProps.type_routing);
            })
            .catch((err) => {
              handleErrorResponse(err, setAuth);
            });
        }
        if (copiedDynamicObj.is_collection) {
          fetchObjectTypesFromApi();
        }
        return fetchDataFromApi(true);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [copiedDynamicObj, collectionDefinition, updatedTypeRouting, section, auth.user, fetchDataFromApi, fetchObjectTypesFromApi, setLoading, setAuth]);

  const onNavigateToCollection = (parsedRowDatum) => {
    onEditAttribute(parsedRowDatum.id);
    navigate(updatedTypeRouting[parsedRowDatum.type] || updatedTypeRouting?.default);
  };

  useEffect(() => {
    setUpdatedTypeRouting(type_routing);
  }, [type_routing]);

  useEffect(() => {
    fetchCollectionDefinitionFromApi();
  }, [fetchCollectionDefinitionFromApi]);

  useEffect(() => {
    if (collectionDefinition) {
      fetchDataFromApi();
    }
  }, [collectionDefinition, fetchDataFromApi]);

  useEffect(() => {
    if (dataLoaded && !formInitialized) {
      const formValues = getFormValues(parsedRowData, objectTypes);
      setInitialValues(formValues);
      setFormInitialized(true);
    }
  }, [dataLoaded, formInitialized, parsedRowData, objectTypes]);

  useEffect(() => {
    const unknowAttributeIds = getUnknowAttributeIds(rowData, collectionDefinition?.attributes || []);
    const newUnknowAttributeIds = unknowAttributeIds.filter((unknowAttributeId) => !extraAttributes.find(({ attribute_id }) => attribute_id === unknowAttributeId));
    if (newUnknowAttributeIds.length > 0) {
      fetchAttributesFromApi(unknowAttributeIds)
        .then((unknowAttributes) => {
          setExtraAttributes(unknowAttributes);
        })
        .catch((err) => {
          handleErrorResponse(err, setAuth);
        });
    }
  }, [collectionDefinition?.attributes, rowData, extraAttributes, fetchAttributesFromApi, setAuth]);

  const onSubmit = useCallback((values) => {
    setLoading(true);
    return Promise.all(
      (parsedRowData || []).map((parsedRowDatum) => {
        const newValues = {};
        if (nameAttribute) {
          newValues[nameAttribute.attribute_id] = values[`${parsedRowDatum.id}-name`];
        }
        if (valueAttribute) {
          newValues[valueAttribute.attribute_id] = values[`${parsedRowDatum.id}-value`];
        }
        if (systemOrderAttribute) {
          newValues[systemOrderAttribute.attribute_id] = values[`${parsedRowDatum.id}-__system_order__`];
        }
        mandatoryAttributeNames.forEach((attributeName) => {
          const attribute = (collectionDefinition?.attributes || []).find(({ name }) => name === attributeName);
          if (attribute) {
            newValues[attribute.attribute_id] = values[`${parsedRowDatum.id}-${attributeName}`];
          }
        });
        extraAttributes.forEach((extraAttribute) => {
          const { name, attribute_id, collection_definition } = extraAttribute;
          if (collection_definition === parsedRowDatum.redirect_collection_definition_id) {
            newValues[attribute_id] = values[`${parsedRowDatum.id}-${name}`];
          }
        });
        const updateBody = {
          json_short_data: JSON.stringify(newValues),
        };
        return updateDynamicTableRow(collectionDefinition?.collection_definition_id, parsedRowDatum.id, updateBody);
      }),
    ).then(() => {
      alert('Update Successfuly');
      setDataLoaded(false);
      setFormInitialized(false);
      return fetchDataFromApi();
    })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [collectionDefinition, parsedRowData, nameAttribute, valueAttribute, systemOrderAttribute, extraAttributes, fetchDataFromApi, setLoading, setAuth]);

  const objectTypeOptions = useMemo(() => (objectTypes || []).reduce((options, objectType) => {
    const { object_type_id, name, redirect, user_created } = objectType;
    if (redirect && !user_created) {
      return [...options, {
        label: name,
        value: object_type_id,
      }];
    }
    return options;
  }, []), [objectTypes]);

  const onToggleKeyAndDescription = () => {
    setShowKeyAndDescription(!showKeyAndDescription);
    localStorage.setItem('dynamicTableAttributeView-showKeyAndDescription-Switch', JSON.stringify(!showKeyAndDescription));
  };

  const onToggleCopyDeleteButtons = () => {
    setShowCopyDeleteButtons(!showCopyDeleteButtons);
    localStorage.setItem('dynamicTableAttributeView-showCopyDeleteButtons-Switch', JSON.stringify(!showCopyDeleteButtons));
  };

  const getObjectTypeIcon = (objectType) => {
    const iconColor = objectType?.user_created ? 'dark' : 'black';
    const iconSize = 'large';
    if (!objectType) {
      return null;
    }
    const { object_type_id } = objectType;
    if (objectType.user_created) {
      return <FolderIcon color={iconColor} fontSize={iconSize} sx={{ mr: 1 }} />;
    }
    if ([1, 2].includes(object_type_id)) {
      return <AbcIcon color={iconColor} fontSize={iconSize} sx={{ mr: 1 }} />;
    }
    if ([3, 4].includes(object_type_id)) {
      return <NumbersIcon color={iconColor} fontSize={iconSize} sx={{ mr: 1 }} />;
    }
    if ([5, 6].includes(object_type_id)) {
      return <ToggleOnIcon color={iconColor} fontSize={iconSize} sx={{ mr: 1 }} />;
    }
    if ([7, 8].includes(object_type_id)) {
      return <ImageIcon color={iconColor} fontSize={iconSize} sx={{ mr: 1 }} />;
    }
    if ([9, 10].includes(object_type_id)) {
      return <VideoFileIcon color={iconColor} fontSize={iconSize} sx={{ mr: 1 }} />;
    }
    if ([11, 12].includes(object_type_id)) {
      return <InsertDriveFileIcon color={iconColor} fontSize={iconSize} sx={{ mr: 1 }} />;
    }
    if ([13, 14].includes(object_type_id)) {
      return <AudioFileIcon color={iconColor} fontSize={iconSize} sx={{ mr: 1 }} />;
    }
    if ([15, 16].includes(object_type_id)) {
      return <DateRangeIcon color={iconColor} fontSize={iconSize} sx={{ mr: 1 }} />;
    }
    if ([17, 18].includes(object_type_id)) {
      return <ImageIcon color={iconColor} fontSize={iconSize} sx={{ mr: 1 }} />;
    }
    if ([19, 20].includes(object_type_id)) {
      return <Rtt color={iconColor} fontSize={iconSize} sx={{ mr: 1 }} />;
    }
    return <FolderIcon color={iconColor} fontSize={iconSize} sx={{ mr: 1 }} />;
  };

  return (
    <MKBox>
      {!isUuid(collection_definition_id) && (
        <MKTypography color="error">Invalid Collection Definition</MKTypography>
      )}
      {!disable_title && (
        <MKBox display="flex" justifyContent="space-between" alignItems="center" py={1}>
          <MKTypography variant="h5">
            {collectionDefinition?.name}
          </MKTypography>
        </MKBox>
      )}
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        enableReinitialize
        innerRef={formRef}
        {...props}
      >
        {({ handleChange, handleBlur, handleSubmit, setFieldValue, errors, values, isSubmitting, dirty, touched }) => {
          return (
            <MKBox component="form" role="form" onSubmit={handleSubmit}>
              <MKBox display="flex" flexDirection="row" position="sticky" top={0} zIndex={10} mb={(hasAddDataRight && (addable || copyable)) ? 1 : 0} sx={{ backgroundColor: '#F1F2F5', borderRadius: '8px', py: 2, px: 2, boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.5)' }}>
                <MKBox>
                  <Button
                    onClick={onClickCancel}
                    variant="outlined"
                    color="secondary"
                  >
                    Back
                  </Button>
                </MKBox>
                {(addable && hasAddDataRight) && (
                <MKBox ml={1}>
                  <Button
                    variant="gradient"
                    color="success"
                    onClick={objectTypes.length > 0 ? onOpenMenu : () => onCreateAttribute('default')}
                  >
                    {add_button_label || 'Add'}
                  </Button>
                </MKBox>
                )}
                {(copyable && !!copiedDynamicObj && hasAddDataRight) && (
                  <MKBox ml={1}>
                    <Button
                      variant="gradient"
                      color="secondary"
                      onClick={onPasteAttribute}
                    >
                      {paste_button_label || 'Paste'}
                    </Button>
                  </MKBox>
                )}
                <MKBox ml={1}>
                  <Button
                    type="submit"
                    variant="gradient"
                    color="info"
                    disabled={!hasEditDataRight || isSubmitting || !dirty}
                  >
                    Save
                  </Button>
                </MKBox>
                <MKBox display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center">
                  <MKTypography variant="body2" sx={{ ml: 1 }}>Key/Description</MKTypography>
                  <Switch checked={showKeyAndDescription} onChange={onToggleKeyAndDescription} />
                </MKBox>
                <MKBox display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center">
                  <MKTypography variant="body2" sx={{ ml: 1 }}>Copy/Delete/Sequence</MKTypography>
                  <Switch checked={showCopyDeleteButtons} onChange={onToggleCopyDeleteButtons} />
                </MKBox>
              </MKBox>
              {(parsedRowData || []).sort(
                (d1, d2) => get(d1, '__system_order__') - get(d2, '__system_order__'),
              ).map((parsedRowDatum) => {
                let objectType = objectTypes.find(({ object_type_id }) => object_type_id === Number(parsedRowDatum.type));
                if (objectType?.redirect) {
                  objectType = objectTypes.find(({ object_type_id }) => object_type_id === objectType.object_type_id - 1);
                }
                if (!objectType) {
                  return null;
                }
                const attributeNameFieldKey = `${parsedRowDatum.id}-name`;
                const attributeValueFieldKey = `${parsedRowDatum.id}-value`;
                const attributeSystemOrderFieldKey = `${parsedRowDatum.id}-__system_order__`;
                const fieldConfig = fields_config[values[attributeNameFieldKey]] || {};
                const relatedExtraAttributes = (extraAttributes || []).filter((extraAttribute) => {
                  return extraAttribute.collection_definition === parsedRowDatum.redirect_collection_definition_id;
                });
                const extraProps = relatedExtraAttributes.reduce((attrProps, relatedExtraAttribute) => {
                  const newAttrProps = { ...attrProps };
                  newAttrProps[relatedExtraAttribute.name] = parsedRowDatum[relatedExtraAttribute.name];
                  return newAttrProps;
                }, {});

                const renderValueField = () => {
                  const { object_type_id, user_created } = objectType || {};
                  if (user_created) {
                    return (
                      <MKBox display="flex" justifyContent="flex-end">
                        <MKTypography variant="body2">
                          <Button
                            variant="gradient"
                            color="secondary"
                            size="small"
                            onClick={() => onNavigateToCollection(parsedRowDatum)}
                          >
                            <ArrowForwardIcon />
                            <ArrowForwardIcon />
                          </Button>
                        </MKTypography>
                      </MKBox>
                    );
                  }
                  let inputType = INPUT_TYPE_TEXTAREA;
                  let inputLabel = 'Value';
                  if ([3, 4].includes(object_type_id)) {
                    inputType = INPUT_TYPE_NUMBER;
                  } else if ([5, 6].includes(object_type_id)) {
                    inputType = INPUT_TYPE_BOOLEAN;
                  } else if ([15, 16].includes(object_type_id)) {
                    inputType = INPUT_TYPE_DATETIME;
                  } else if ([7, 8].includes(object_type_id)) {
                    inputType = INPUT_TYPE_IMAGE;
                    inputLabel = 'Image File';
                  } else if ([9, 10].includes(object_type_id)) {
                    inputType = INPUT_TYPE_VIDEO;
                    inputLabel = 'Video File';
                  } else if ([11, 12].includes(object_type_id)) {
                    inputType = INPUT_TYPE_FILE;
                    inputLabel = 'Document File';
                  } else if ([13, 14].includes(object_type_id)) {
                    inputType = INPUT_TYPE_AUDIO;
                    inputLabel = 'Audio File';
                  } else if ([17, 18].includes(object_type_id)) {
                    inputType = INPUT_TYPE_IMAGE;
                    inputLabel = 'Media File';
                  } else if ([19, 20].includes(object_type_id)) {
                    inputType = INPUT_TYPE_RICH_TEXT;
                  }
                  return (
                    <InputField
                      name={attributeValueFieldKey}
                      value={values[attributeValueFieldKey]}
                      label={inputLabel}
                      type={inputType}
                      setFieldValue={setFieldValue}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      disabled={!hasEditDataRight}
                      button_text="Upload"
                      {...([17, 18].includes(object_type_id) ? {
                        accept_file_types: ['image/*', 'video/*'],
                      } : {})}
                      {...fieldConfig}
                      {...extraProps}
                    />
                  );
                };
                return (
                  <MKBox p={2} mb={2} borderRadius="lg" bgColor="white" shadow="lg">
                    <MKBox display="flex" flexDirection="row" justifyContent="space-between">
                      <MKBox display="flex" flexDirection="row" alignItems="center">
                        { getObjectTypeIcon(objectType) }
                        <MKTypography fontSize="16px" color="black">{(objectType.name || '')}</MKTypography>
                      </MKBox>
                      { showCopyDeleteButtons && (
                      <MKBox display="flex" flexDirection="row" alignItems="center">
                        {(copyable && hasAddDataRight) && (
                        <MKBox sx={{ mr: 1 }}>
                          <Button
                            size="medium"
                            variant="gradient"
                            color="dark"
                            onClick={() => onCopyAttribute(parsedRowDatum.id)}
                          >
                            {copy_button_label || 'Copy'}
                          </Button>
                        </MKBox>
                        )}
                        {(deletable && hasDeleteDataRight) && (
                        <MKBox sx={{ mr: 1 }}>
                          <Button
                            size="medium"
                            variant="gradient"
                            color="error"
                            onClick={() => {
                              if (window.confirm('Do you confirm to delete this?')) {
                                return Promise.resolve(onDeleteAttribute(parsedRowDatum.id));
                              }
                              return Promise.resolve();
                            }}
                          >
                            {delete_button_label || 'Delete'}
                          </Button>
                        </MKBox>
                        )}
                        {!!systemOrderAttribute && (
                          <MKBox width={60} mr={1}>
                            <InputField
                              name={attributeSystemOrderFieldKey}
                              label={<NumbersIcon fontSize="small" />}
                              type={INPUT_TYPE_NUMBER}
                              size="small"
                              value={values[attributeSystemOrderFieldKey]}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              disabled={!hasEditDataRight}
                            />
                          </MKBox>
                        )}
                      </MKBox>
                      )}
                    </MKBox>
                    {!!nameAttribute && (
                      <MKBox mt={2}>
                        <InputField
                          name={attributeNameFieldKey}
                          label="Name"
                          value={values[attributeNameFieldKey]}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          disabled={objectType.user_created || !hasEditDataRight}
                        />
                      </MKBox>
                    )}
                    {mandatoryAttributeNames.map((attributeName, idx) => {
                      if (!showKeyAndDescription) {
                        return null;
                      }
                      const attributeFieldKey = `${parsedRowDatum.id}-${attributeName}`;
                      const attribute = (collectionDefinition?.attributes || []).find(({ name }) => name === attributeName);
                      return attribute ? (
                        <MKBox key={idx} mt={2}>
                          <InputField
                            name={attributeFieldKey}
                            label={startCase(attributeName)}
                            value={values[attributeFieldKey]}
                            type={attributeName === 'description' ? INPUT_TYPE_TEXTAREA : INPUT_TYPE_TEXT}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            disabled={!hasEditDataRight}
                          />
                        </MKBox>
                      ) : null;
                    })}
                    {(relatedExtraAttributes || []).map((extraAttribute, idx) => {
                      const attributeFieldKey = `${parsedRowDatum.id}-${extraAttribute.name}`;
                      return (
                        <MKBox key={idx} mt={2}>
                          <EditAttributeField
                            attribute={{ ...extraAttribute, attribute_id: attributeFieldKey }}
                            value={values[attributeFieldKey]}
                            setFieldValue={setFieldValue}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            disabled={!hasEditDataRight}
                          />
                        </MKBox>
                      );
                    })}
                    <MKBox mt={2}>
                      {!!valueAttribute && renderValueField()}
                    </MKBox>
                  </MKBox>
                );
              })}
              <Grid container justifyContent="flex-end" position="sticky" bottom={16}>
                <Grid item xs={12} md={6} xl={4}>
                  <MKTypography variant="caption" color="error">
                    {errors.form}
                    &nbsp;
                  </MKTypography>
                </Grid>
              </Grid>
              <div ref={bottomDiv} />
            </MKBox>
          );
        }}
      </Formik>
      <AnchorMenu
        items={objectTypeOptions}
        onClickItem={onCreateAttribute}
        anchor={menuAnchor}
        setAnchor={setMenuAnchor}
      />
    </MKBox>
  );
};

DynamicTableAttributeViewSection.propTypes = {
  collection_definition_id: PropTypes.string,
  addable: PropTypes.bool,
  deletable: PropTypes.bool,
  copyable: PropTypes.bool,
  type_routing: PropTypes.object,
  fields_config: PropTypes.object,
  add_button_label: PropTypes.string,
  delete_button_label: PropTypes.string,
  copy_button_label: PropTypes.string,
  paste_button_label: PropTypes.string,
  disable_title: PropTypes.bool,
  section: PropTypes.object,
  setLoading: PropTypes.func,
};

export default withLoading(DynamicTableAttributeViewSection);
